import { zodResolver } from '@hookform/resolvers/zod';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid,
    ToggleButton, ToggleButtonGroup, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect, useMemo, useState } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { CrmLeadService } from '../../services/crm-lead.service';
import { LeadStatusResponse } from '@layouts/pages/Settings/general/lead-status/services/responses/lead-status.response';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { Combo } from '@types_main/combo';
import { apiErrorMessage } from '@functions/api-error-message';
import { PersonService } from '@layouts/pages/CRM/people/services/person.service';
import { PersonOptionResponse } from '@layouts/pages/CRM/people/services/responses/person.response';
import { PeopleModal } from '@layouts/pages/CRM/people/components/people.modal';
import { CompanyService } from '@layouts/pages/CRM/companies/services/company.service';
import { CompanyOptionResponse } from '@layouts/pages/CRM/companies/services/responses/company.response';
import { CompaniesModal } from '@layouts/pages/CRM/companies/components/companies.modal';
import { companyOptionLabel } from '@layouts/pages/CRM/companies/company-form';
import { formatPhone } from '@functions/format-phone';
import { firstErrorMessage } from '@functions/first-error-message';

/// Lead de pessoa ou da própria empresa.
type LeadOwner = 'person' | 'company';

interface Props {
    open: boolean;
    columnId: string;
    statuses: LeadStatusResponse[];
    onClose: () => void;
    onCreated: () => void;
}

export const CrmAddLeadModal = ({ open, columnId, statuses, onClose, onCreated }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();

    const [owner, setOwner] = useState<LeadOwner>('person');
    const [people, setPeople] = useState<PersonOptionResponse[]>([]);
    const [companies, setCompanies] = useState<CompanyOptionResponse[]>([]);
    const [registering, setRegistering] = useState(false);

    const schema = z.object({
        ownerId: z.string().min(1, t('validacao.obrigatorio')),
        statusId: z.string().nullable().optional(),
        description: z.string().max(500).optional(),
    });

    type LeadForm = z.infer<typeof schema>;

    const form = useForm<LeadForm>({
        resolver: zodResolver(schema),
        defaultValues: { ownerId: '', statusId: '', description: '' },
    });

    const { handleSubmit, reset, setValue, formState: { errors } } = form;

    const loadPeople = () =>
        new PersonService()
            .options()
            .then((res) => setPeople(res.data.data ?? []))
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 3000));

    const loadCompanies = () =>
        new CompanyService()
            .options()
            .then((res) => setCompanies(res.data.data ?? []))
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 3000));

    useEffect(() => {
        if (!open) return;

        setOwner('person');
        reset({
            ownerId: '',
            statusId: statuses.find((s) => s.isDefault)?.id ?? '',
            description: '',
        });
        loadPeople();
        loadCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, statuses]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
    }, [errors]);

    const ownerOptions: Combo[] = useMemo(
        () => (owner === 'person'
            ? people.map((person) => ({
                value: person.id,
                label: `${person.name}${person.phone ? ` · ${formatPhone(person.phone)}` : ''}`,
            }))
            : companies.map((company) => ({ value: company.id, label: companyOptionLabel(company) }))),
        [owner, people, companies]);

    const statusOptions: Combo[] = statuses.map((s) => ({ label: s.name, value: s.id }));

    const changeOwner = (next: LeadOwner) => {
        setOwner(next);
        setValue('ownerId', '');
    };

    /// O cadastro feito pelo + já volta escolhido no seletor.
    const registered = (id: string) => {
        (owner === 'person' ? loadPeople() : loadCompanies())
            .then(() => setValue('ownerId', id, { shouldValidate: true }));
    };

    const saveChanges = async (data: LeadForm) => {
        const person = owner === 'person' ? people.find((item) => item.id === data.ownerId) : null;

        setLoading(dispatch, true);
        try {
            // Lead de empresa: o servidor copia nome, telefone e e-mail do cadastro dela.
            await new CrmLeadService().create({
                columnId,
                contactName: person?.name ?? '',
                contactPhone: person?.phone ?? '',
                contactEmail: person?.email || undefined,
                companyId: owner === 'company' ? data.ownerId : undefined,
                statusId: data.statusId || undefined,
                description: data.description || undefined,
            });
            showAlert(t('general.cadastro'), 'success', 2000);
            onCreated();
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
                <DialogTitle>{t('crmKanban.adicionarLead')}</DialogTitle>
                <FormProvider {...form}>
                    <form onSubmit={handleSubmit(saveChanges)}>
                        <DialogContent style={smallDialog}>
                            <Grid container spacing={2}>
                                <Form.Field size={12}>
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={owner}
                                        onChange={(_, value) => value && changeOwner(value)}
                                    >
                                        <ToggleButton value="person" sx={{ px: 2.5 }}>{t('crmKanban.leadDePessoa')}</ToggleButton>
                                        <ToggleButton value="company" sx={{ px: 2.5 }}>{t('crmKanban.leadDeEmpresa')}</ToggleButton>
                                    </ToggleButtonGroup>
                                </Form.Field>

                                <Form.Field size={8}>
                                    <FormControl fullWidth>
                                        <Form.Label>
                                            {t(owner === 'person' ? 'crmKanban.pessoa' : 'crmKanban.empresa')}
                                            <Form.MandatoryIcon />
                                        </Form.Label>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Form.AutoCompleteSelect
                                                    key={owner}
                                                    name="ownerId"
                                                    options={ownerOptions}
                                                />
                                            </Box>
                                            <Tooltip
                                                title={t(owner === 'person' ? 'crmKanban.cadastrarPessoa' : 'crmKanban.cadastrarEmpresa')}
                                            >
                                                <MDButton
                                                    variant="gradient"
                                                    color="dark"
                                                    iconOnly
                                                    onClick={() => setRegistering(true)}
                                                    sx={{ width: 43, minWidth: 43, height: 43 }}
                                                >
                                                    <AddIcon />
                                                </MDButton>
                                            </Tooltip>
                                        </Box>
                                        <Form.ErrorMessage field="ownerId" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={4}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('crmKanban.status')}</Form.Label>
                                        <Form.AutoCompleteSelect name="statusId" options={statusOptions} />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={12}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('general.descricao')}</Form.Label>
                                        <Form.TextFields
                                            name="description"
                                            multiline
                                            minRows={2}
                                            size="small"
                                            inputProps={{ maxLength: 500 }}
                                        />
                                    </FormControl>
                                </Form.Field>
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Box>
                                <MDButton sx={{ marginRight: '5px' }} variant="gradient" color="dark" onClick={onClose}>
                                    {t('general.cancelar')}
                                </MDButton>
                                <MDButton variant="gradient" color="success" type="submit">
                                    {t('general.salvar')}
                                </MDButton>
                            </Box>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>

            {owner === 'person' ? (
                <PeopleModal
                    open={registering}
                    edit={null}
                    handleModal={() => setRegistering(false)}
                    getAll={() => undefined}
                    onSaved={(person) => registered(person.id)}
                />
            ) : (
                <CompaniesModal
                    open={registering}
                    edit={null}
                    handleModal={() => setRegistering(false)}
                    getAll={() => undefined}
                    onSaved={(company) => registered(company.id)}
                />
            )}
        </>
    );
};
